import * as React from 'react';
import Media from 'react-media';

import Imgix from 'js/components/Imgix';
import connectToStores from 'js/lib/connectToStores';
import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { useParams } from 'js/lib/useRouter';
import type UserAgentInfo from 'js/lib/useragent';

import { Grid, Typography2 } from '@coursera/cds-core';
import { StyleSheet, breakPoint, css } from '@coursera/coursera-ui';

// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { INDIA_ANNUAL_PROMO_MID_2024_SLUG } from 'bundles/coursera-plus-landing-page/components/MarketingHome';
import {
  getPartnerLogos,
  getPartnerLogosForCPlusIndiaPromoMid2024PromoPage,
  getPartnerLogosForGenerativeAILandingPage,
} from 'bundles/coursera-plus/components/constants';
import type { PartnerLogo } from 'bundles/coursera-plus/components/constants';
import { getCopy, isGenerativeAILandingPage } from 'bundles/coursera-plus/utils/courseraPlusUtils';
import { BASE_STYLES } from 'bundles/design-system/components/container-styles';
import ApplicationStoreClass from 'bundles/ssr/stores/ApplicationStore';

import _t from 'i18n!nls/coursera-plus-landing-page';

const styles = StyleSheet.create({
  containerWrapper: {
    ...BASE_STYLES.containerWrapper,
  },
  container: {
    margin: '0 auto',
    padding: '20px 0 40px 0',
    [`@media (max-width: ${breakPoint.md - 1}px) and (min-width: ${breakPoint.sm}px)`]: {
      padding: '36px 0',
    },
    [`@media (max-width: ${breakPoint.sm - 1}px)`]: {
      padding: '24px 0',
    },
    ...BASE_STYLES.container,
  },
  title: {
    ...BASE_STYLES.title,
    fontSize: '1.5rem !important',
    paddingBottom: '24px',
    fontFamily: 'OpenSans, Arial, sans-serif !important', // <H3> provides the right sizing but is OpenSans-Light instead
    [`@media (max-width: ${breakPoint.lg - 1}px) and (min-width: ${breakPoint.md}px)`]: {
      fontSize: '20px !important',
      lineHeight: '24px !important',
      paddingBottom: 0,
    },
    [`@media (max-width: ${breakPoint.md - 1}px)`]: {
      // sadly, @media doesn't add any specificity, so we need to use !important
      fontSize: '14px !important',
      lineHeight: '20px !important',
      paddingBottom: 0,
    },
  },
  partnerLogos: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    [`@media (max-width: ${breakPoint.lg - 1}px)`]: {
      maxWidth: '1132px',
      justifyContent: 'center',
    },
  },
  partnerLogo: {
    display: 'inline-block',
    margin: '0 10px',
    [`@media (max-width: ${breakPoint.lg - 1}px) and (min-width: ${breakPoint.sm}px)`]: {
      margin: '12px 24px',
    },
    [`@media (max-width: ${breakPoint.sm - 1}px)`]: {
      margin: '12px 6px',
    },
  },
  logo: {
    [`@media (max-width: ${breakPoint.lg - 1}px) and (min-width: ${breakPoint.md}px)`]: {
      height: '27px',
    },
    [`@media (max-width: ${breakPoint.md - 1}px)`]: {
      height: '22px',
    },
  },
  umich: {
    [`@media (max-width: ${breakPoint.md - 1}px)`]: {
      height: '27px',
    },
  },
  duke: {
    // Necessary because Duke's logo on desktop is larger than other partners'
    [`@media (min-width: ${breakPoint.lg}px)`]: {
      height: '32px',
      verticalAlign: 'baseline',
    },
  },
});

const renderPartnerLogoImages = (partnerLogos: Array<PartnerLogo>): React.ReactNode => {
  return partnerLogos.map((partner) => {
    return (
      <span {...css(styles.partnerLogo)} key={partner.id}>
        <Imgix
          {...css(partner.id === 'umich' ? styles.umich : styles.logo, partner.id === 'duke' && styles.duke)}
          alt={partner.title}
          src={partner.image}
          maxHeight={partner.height}
        />
      </span>
    );
  });
};

type PropsFromStore = {
  userAgent: UserAgentInfo;
};

type PropsToComponent = PropsFromStore;

const CourseraPlusPartnerLogos = ({ userAgent }: PropsToComponent) => {
  const { slug } = useParams();
  const { numberOfPartnersNoPlusSign } = getCopy();
  const sectionTitle = _t('Learn from {numberOfPartners} leading universities and companies with Coursera Plus');

  return (
    <div {...css(styles.containerWrapper)}>
      <Grid className={css(styles.container).className}>
        <Typography2 className={css(styles.title).className} component="h2">
          <FormattedMessage message={sectionTitle} numberOfPartners={<strong>{numberOfPartnersNoPlusSign}</strong>} />
        </Typography2>
        <Media query={{ maxWidth: breakPoint.lg - 1 }} defaultMatches={userAgent?.isMobileBrowser}>
          {(matches) => {
            let partnerLogos = getPartnerLogos();

            if (isGenerativeAILandingPage(slug)) {
              partnerLogos = getPartnerLogosForGenerativeAILandingPage();
            } else if (slug === INDIA_ANNUAL_PROMO_MID_2024_SLUG) {
              partnerLogos = getPartnerLogosForCPlusIndiaPromoMid2024PromoPage();
            }

            // For tablet screens and smaller, show partner logos across two rows instead of just one
            if (matches) {
              const halfPoint = Math.ceil(partnerLogos.length / 2);
              const partnerLogoRows = [
                partnerLogos.slice(0, halfPoint),
                partnerLogos.slice(halfPoint, partnerLogos.length),
              ];
              return (
                <div>
                  {partnerLogoRows.map((partnerLogoRow, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <div key={`partner-logo-row-${i}`} {...css(styles.partnerLogos)}>
                      {renderPartnerLogoImages(partnerLogoRow)}
                    </div>
                  ))}
                </div>
              );
            } else {
              return <div {...css(styles.partnerLogos)}>{renderPartnerLogoImages(partnerLogos)}</div>;
            }
          }}
        </Media>
      </Grid>
    </div>
  );
};

export default connectToStores<PropsToComponent, {}>([ApplicationStoreClass], (ApplicationStore, props) => ({
  ...props,
  userAgent: ApplicationStore.getUserAgent(),
}))(CourseraPlusPartnerLogos);
